import { ChangeDetectorRef, Component, HostBinding, OnInit, ViewChild } from '@angular/core';
import { Label, ChartDataset, Color } from '@michelin/charts';
import { PaginatorFetcher, PaginatorFilters, TableComponent, TableConfig, ThemingService } from '@michelin/theme';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import {
  OrderAttendanceReportDto,
  OrderDeliveryReportDto,
  OrderItemAttendanceReportDto
} from 'src/app/shared/models/dashboard/dashboard-dto';

import { PAGINATION_SIZES } from 'src/app/shared/utils/constants';
import { DistributorDto } from 'src/app/shared/models/distributor/distributor-dto';
import { DistributorService } from 'src/app/shared/services/distributor/distributor.service';
import { forkJoin, of } from 'rxjs';
import { OrderService } from 'src/app/shared/services/order/order.service';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { AutomakerDto } from 'src/app/shared/models/automaker/automaker-dto';
import { AutomakerService } from 'src/app/shared/services/automaker/automaker.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrl: './dashboard.component.scss',
    standalone: false
})
export class DashboardComponent implements OnInit {
  @HostBinding('class') className = this.themingService.theme.className;

  @ViewChild(TableComponent) table: TableComponent<OrderItemAttendanceReportDto>;

  public filterForm: FormGroup;

  formatCurrency = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  loading = false;
  showCommission = false;

  userDistributor: number | null = null;
  userAutomaker: number | null = null;

  automakers: AutomakerDto[] = [];
  distributors: DistributorDto[] = [];

  graphicsData: {
    orderDeliveryItems: OrderDeliveryReportDto[];
    orderAttendanceItems: OrderAttendanceReportDto[];
  } = {
    orderDeliveryItems: [],
    orderAttendanceItems: []
  };

  totalQuantity = 0;
  totalQuantityServed = 0;
  totalQuantityCanceled = 0;
  totalValueServed = 0;
  commissionValue = 0;

  pieChartLabels: Label[] = [];
  pieChartDatasets: ChartDataset[] = [];

  barChartLabels: Label[] = [];
  barChartDatasets: ChartDataset[] = [];
  barChartColors: Color[] = [
    {
      backgroundColor: 'rgba(39,80,155,0.6)',
      borderColor: 'rgba(39,80,155,1)',
      borderWidth: 1
    },
    {
      backgroundColor: 'rgba(69,185,165,1)',
      borderColor: 'rgba(69,185,165,0.8)',
      borderWidth: 1
    },
    {
      backgroundColor: 'rgba(185, 77, 69, 1)',
      borderColor: 'rgba(185, 77, 69, 0.8)',
      borderWidth: 1
    }
  ];

  tableConfig: TableConfig<OrderItemAttendanceReportDto> = {
    paginator: {
      pageSize: 10,
      position: 'bottom',
      pageSizeOptions: PAGINATION_SIZES
    },
    columns: {
      codeOrder: { cell: 'codeOrderCell', header: 'Cod. Pedido' },
      codeOrderClient: { cell: 'codeOrderClientCell', header: 'Cod. Pedido (Cliente)' },
      label: { cell: (tableRow) => tableRow.label, header: 'Mês Ref.' },
      automaker: { cell: (tableRow) => tableRow.automaker.companyName, header: 'OE' },
      distributor: { cell: (tableRow) => tableRow.distributor.companyName, header: 'DE' },
      date: {
        cell: (tableRow) => {
          if (!tableRow.date) return '-';
          const d = new Date(tableRow.date);
          return d.toLocaleDateString('pt-BR');
        },
        header: 'Data NF.'
      },
      timeToDelivery: { cell: (tableRow) => tableRow.timeToDelivery, header: 'Entrega', align: 'center' },
      cai: { cell: (tableRow) => tableRow.cai, header: 'CAI' },
      // partNumber: { cell: (tableRow) => tableRow.partNumber, header: 'Part #' },
      description: { cell: (tableRow) => tableRow.description, header: 'Desc.' },
      status: { cell: (tableRow) => tableRow.statusName, header: 'Status' },
      qty: { cell: (tableRow) => tableRow.quantity.toString(), header: 'Qtd.' },
      qtyServed: { cell: (tableRow) => tableRow.quantityServed.toString(), header: 'Qtd. Atendido' },
      // itemValue: { cell: (tableRow) => this.formatCurrency.format(tableRow.itemValue), header: 'Valor do Item' },
      totalprice: { cell: (tableRow) => this.formatCurrency.format(tableRow.totalPrice), header: 'Valor Total' },
      // commissionPercentage: {
      //   display: this.showCommission,
      //   cell: (tableRow) => `${tableRow.automakerCommissionPercentage}%`,
      //   header: 'Comissão (%)'
      // },
      commissionValue: {
        display: this.showCommission,
        cell: (tableRow) => this.formatCurrency.format(tableRow.automakerCommissionValue),
        header: 'Comissão'
      }
    }
  };

  fetcher: PaginatorFetcher<OrderItemAttendanceReportDto> = (filters: PaginatorFilters) =>
    this.orderService.getOrderItemAttendanceReport(filters);

  constructor(
    private readonly distributorService: DistributorService,
    private readonly automakerService: AutomakerService,
    private readonly orderService: OrderService,
    private readonly authService: AuthService,
    private readonly themingService: ThemingService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly form: FormBuilder,
    private readonly router: Router
  ) {
    this.filterForm = form.group({
      distributor: form.control(''),
      automaker: form.control(''),
      dateStart: form.control(''),
      dateEnd: form.control('')
    });
  }

  get distributor(): FormControl {
    return this.filterForm.get('distributor') as FormControl;
  }

  get automaker(): FormControl {
    return this.filterForm.get('automaker') as FormControl;
  }

  get dateStart(): FormControl {
    return this.filterForm.get('dateStart') as FormControl;
  }

  get dateEnd(): FormControl {
    return this.filterForm.get('dateEnd') as FormControl;
  }

  get distributorOptions(): DistributorDto[] {
    const user = this.authService.getUserData();
    if (['AM', 'GRV'].includes(user?.userProfile?.name?.trim())) {
      const allowedDistributorIds = user?.managedAccounts.map((account) => account.distributorId) || [];
      return this.distributors.filter((d) => allowedDistributorIds.includes(d.id));
    }
    return this.distributors;
  }

  setDefaultData() {
    const today = new Date();
    const startDate = new Date(today.getFullYear(), 0, 1);
    const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    this.dateStart.setValue(startDate);
    this.dateEnd.setValue(endDate);

    const user = this.authService.getUserData();

    if (user?.userProfile?.userType === 'DISTRIBUTOR' && user?.company) {
      this.userDistributor = this.distributors.find((d) => d.company?.id === user.company?.id)?.id || -1;
    }

    if (user?.userProfile?.userType === 'AUTOMAKER' && user?.company) {
      this.userAutomaker = this.automakers.find((d) => d.company?.id === user.company?.id)?.id || -1;
    }

    if (['AUTOMAKER', 'MICHELIN'].includes(user?.userProfile?.userType)) {
      this.showCommission = true;
      if (this.tableConfig.columns.commissionValue) {
        this.tableConfig.columns.commissionValue.display = true;
      }
    }
  }

  companyDisplayName(data: DistributorDto) {
    return data?.company?.tradeName || '';
  }

  redirectToOrder(item: OrderItemAttendanceReportDto) {
    this.router.navigate([`/orders/${item.orderId}`]);
  }

  getTableRowStatusIcon(tableRowStatus: string): string {
    return { draft: 'edit', hold: 'hourglass_empty', published: 'check_circle' }[tableRowStatus];
  }

  getTableRowStatusLabel(tableRowStatus: string): string {
    return { draft: 'Draft', hold: 'On Hold', published: 'Published' }[tableRowStatus];
  }

  get hasBarChartData(): boolean {
    return this.barChartLabels.length > 0 && this.barChartDatasets.length > 0;
  }

  get hasPieChartData(): boolean {
    return this.pieChartLabels.length > 0 && this.pieChartDatasets.length > 0 && 
           this.pieChartDatasets.some(ds => ds.data && ds.data.length > 0);
  }

  setGraphicsData() {
    this.barChartLabels = [];
    this.barChartDatasets = [];

    this.totalQuantity = 0;
    this.totalQuantityServed = 0;
    this.totalQuantityCanceled = 0;
    this.totalValueServed = 0;

    this.graphicsData.orderAttendanceItems.forEach((item: OrderAttendanceReportDto) => {
      this.barChartLabels.push(item.label);
    });

    this.barChartDatasets.push({
      label: 'Pneus Ingressados',
      data: this.graphicsData.orderAttendanceItems.map((item: OrderAttendanceReportDto) => item.quantity)
    });
    this.barChartDatasets.push({
      label: 'Pneus Atendidos',
      data: this.graphicsData.orderAttendanceItems.map((item: OrderAttendanceReportDto) => item.quantityServed)
    });
    this.barChartDatasets.push({
      label: 'Pneus Cancelados',
      backgroundColor: '#ab3526',
      data: this.graphicsData.orderAttendanceItems.map((item: OrderAttendanceReportDto) => item.quantityCanceled)
    });

    this.pieChartLabels = this.graphicsData.orderDeliveryItems.map((item: OrderDeliveryReportDto) => item.deliveryTime);
    this.pieChartDatasets = [
      {
        data: this.graphicsData.orderDeliveryItems.map((item: OrderDeliveryReportDto) => item.total),
        label: ''
      }
    ];

    this.graphicsData.orderAttendanceItems.forEach((item: OrderAttendanceReportDto) => {
      this.totalQuantity += item.quantity;
      this.totalQuantityServed += item.quantityServed;
      this.totalQuantityCanceled += item.quantityCanceled;
      this.totalValueServed += item.quantityServedValue;
    });
  }

  applyFilters() {
    const distributorId = this.userDistributor || this.distributor.value?.id;
    const automakerId = this.userAutomaker || this.automaker.value?.id;
    const dateStart = this.dateStart.value.toISOString().split('T')[0];
    const dateEnd = this.dateEnd.value.toISOString().split('T')[0];

    this.loading = true;

    const requests = {
      orderDeliveryItems: this.orderService.getDeliveryReport(dateStart, dateEnd, distributorId, automakerId),
      orderAttendanceItems: this.orderService.getAttendanceReport(dateStart, dateEnd, distributorId, automakerId),
      automakerCommissionValue: of({ commissionValue: 0 })
    };

    if (this.showCommission) {
      requests.automakerCommissionValue = this.orderService.getAutomakerCommissionValue(dateStart, dateEnd, distributorId, automakerId);
    }

    forkJoin(requests)
      .subscribe({
        next: (data) => {
          this.graphicsData = data;
          this.commissionValue = data.automakerCommissionValue?.commissionValue || 0;
          this.setGraphicsData();
          this.loading = false;
          this.changeDetector.detectChanges();

          const tableFilters: any = {};
          tableFilters.distributorId = distributorId;
          tableFilters.automakerId = automakerId;
          tableFilters.dateStart = dateStart;
          tableFilters.dateEnd = dateEnd;

          if (this.table) {
            this.table.patchExtraFilters(tableFilters, true);
          }
        },
        error: () => {
          this.loading = false;
          this.changeDetector.detectChanges();
        }
      });
  }

  resetFilters() {
    this.filterForm.reset();
    this.setDefaultData();
    this.applyFilters();
  }

  ngOnInit() {
    this.loading = true;
    forkJoin({
      distributors: this.distributorService.findAll(),
      automakers: this.automakerService.findAll()
    })
      .subscribe({
        next: (data) => {
          this.automakers = data.automakers;
          this.distributors = data.distributors;
          this.setDefaultData();
          this.applyFilters();
        },
        error: () => {
          this.loading = false;
        }
      });
  }
}
